import { useCanvasFlowSessions } from "@/api/canvas";
import { useDeleteFlowSession, useFlowSessionById } from "@/api/flow_sessions";
import { ChatDot } from "@/components/rail-kit/ChatDot";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { DraggableData } from "@/dnd/ids";
import { RailRowMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { emitCanvasOpenFloatingSession } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { useCanvasNodesMirror } from "@/pages/canvas/context/CanvasNodesMirrorContext";
import { emitCanvasFlowSessionDeleted } from "@/pages/canvas/context/CanvasProcessContext";
import { cn } from "@/utils";
import type { FlowSession } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useQueryClient } from "@tanstack/react-query";
import { Bot, Crosshair, ExternalLink, Loader2, Trash2 } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  dispatchCrossScopeSelect,
  useClearExplorerSelectionListener,
  useCrossScopeSelectListener,
} from "./canvasRailSelectionEvents";
import { SectionEmptyState } from "./rows/SectionHeader";

// ─── Types ────────────────────────────────────────────────────────────

type SectionKind = "chat" | "agent-task";

interface SessionMenuState {
  x: number;
  y: number;
  kind: SectionKind;
  /** IDs the action should affect when invoked (1 for single right-click,
   *  N when right-clicking inside an existing multi-selection). */
  targetIds: string[];
}

interface ConfirmDeleteState {
  kind: SectionKind;
  targets: FlowSession[];
}

const SECTION_LABEL: Record<SectionKind, { singular: string; plural: string }> =
  {
    chat: { singular: "chat", plural: "chats" },
    "agent-task": { singular: "agent task", plural: "agent tasks" },
  };

/**
 * Chats scope.
 *
 * The header + collapse chevron from the old "CHATS" accordion are gone —
 * the scope chip already tells the user what they're looking at, so the
 * pane drops straight into the list. The most-used action ("start a new
 * chat") gets the top row of the list with primary-tinted chrome so it
 * reads as both an entry point and a list item, not a hidden +.
 *
 * Sub-agent tasks render below in their own collapsible section that
 * uses the same `SectionHeader` chrome as Canvas Contents — they're a
 * different mental model (machine-spawned, transient) so they get a
 * distinct icon treatment, but the section structure stays consistent
 * with the rest of the explorer.
 *
 * Selection + right-click semantics mirror Canvas Contents:
 *   - Click selects (replaces selection); cmd/ctrl-click toggles;
 *     shift-click extends a contiguous range over the *visible* row
 *     order. Selection is per-section — chats and agent tasks each
 *     own their own set so the two mental models can't accidentally
 *     mix in a single bulk action.
 *   - Right-click on a row scopes its menu to the selection if the
 *     row is part of one (>1), else to that single row. Empty-area
 *     right-click in either section gets a "Delete all" entry.
 *   - Destructive actions ALWAYS funnel through an AlertDialog so an
 *     accidental click can't nuke a long-running agent task.
 */
export function CanvasRailChats({
  canvasId,
  searchQuery,
  canEdit,
}: {
  canvasId: string;
  searchQuery: string;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const deleteFlowSession = useDeleteFlowSession();
  const { data: sessions = [], isLoading: sessionsLoading } =
    useCanvasFlowSessions(canvasId, {
      enabled: !!canvasId,
    });

  const mainSessions = useMemo(
    () => sessions.filter((s) => !s.action_item_id),
    [sessions],
  );
  const agentTaskSessions = useMemo(
    () => sessions.filter((s) => !!s.action_item_id),
    [sessions],
  );

  // Live mirror of every canvas node, published by `CanvasPage`. We use
  // this to resolve "is this chat placed on the canvas?" — a chat counts
  // as placed when at least one `chatSession` node references its
  // `flowSessionId`. The Locate button uses the resolved nodeId as the
  // focus target; absent placement, the button greys out.
  const rawNodes = useCanvasNodesMirror(canvasId);
  const placedNodeBySessionId = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of rawNodes) {
      if (n.type !== "chatSession") continue;
      const sid = (n.data as { flowSessionId?: string } | undefined)
        ?.flowSessionId;
      if (sid && !map.has(sid)) map.set(sid, n.id);
    }
    return map;
  }, [rawNodes]);

  // Per-section selection state. Splitting the two sets keeps shift-range
  // anchored within a single section and prevents a cross-section bulk
  // delete that mixes user chats with machine-spawned tasks.
  const [selectedChatIds, setSelectedChatIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(
    new Set(),
  );
  const lastChatIdRef = useRef<string | null>(null);
  const lastTaskIdRef = useRef<string | null>(null);

  const [menu, setMenu] = useState<SessionMenuState | null>(null);
  const [confirm, setConfirm] = useState<ConfirmDeleteState | null>(null);

  // ─── Cross-scope deselect plumbing ──────────────────────────────
  // The surface-rail click broadcasts `canvas-explorer:deselect`
  // and other scopes (e.g. Canvas Contents) broadcast
  // `canvas-explorer:cross-scope-select` when they record a new
  // selection. Either signal clears chats so the user sees one
  // visible "selected" cluster across the whole pane.
  const clearChatSelections = useCallback(() => {
    setSelectedChatIds((prev) => (prev.size > 0 ? new Set() : prev));
    setSelectedTaskIds((prev) => (prev.size > 0 ? new Set() : prev));
    lastChatIdRef.current = null;
    lastTaskIdRef.current = null;
  }, []);
  useClearExplorerSelectionListener(clearChatSelections);
  useCrossScopeSelectListener("chats", clearChatSelections);

  // ─── Selection helpers ──────────────────────────────────────────
  const selectionFor = (kind: SectionKind) =>
    kind === "chat" ? selectedChatIds : selectedTaskIds;
  const setSelectionFor = (kind: SectionKind, next: Set<string>) => {
    if (kind === "chat") setSelectedChatIds(next);
    else setSelectedTaskIds(next);
    // Any non-empty selection inside chats kicks the contents scope
    // (and any future cross-scope subscribers) into clearing theirs.
    if (next.size > 0) dispatchCrossScopeSelect("chats");
  };
  const lastIdRefFor = (kind: SectionKind) =>
    kind === "chat" ? lastChatIdRef : lastTaskIdRef;
  const orderFor = (kind: SectionKind) =>
    (kind === "chat" ? mainSessions : agentTaskSessions).map((s) => s.id);

  const handleSelect = (
    kind: SectionKind,
    sessionId: string,
    e: React.MouseEvent,
  ) => {
    const order = orderFor(kind);
    const lastRef = lastIdRefFor(kind);
    const current = selectionFor(kind);

    if (e.metaKey || e.ctrlKey) {
      const next = new Set(current);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      lastRef.current = sessionId;
      setSelectionFor(kind, next);
      return;
    }
    if (e.shiftKey && lastRef.current) {
      const a = order.indexOf(lastRef.current);
      const b = order.indexOf(sessionId);
      if (a !== -1 && b !== -1) {
        const [start, end] = a < b ? [a, b] : [b, a];
        setSelectionFor(kind, new Set(order.slice(start, end + 1)));
        return;
      }
    }
    setSelectionFor(kind, new Set([sessionId]));
    lastRef.current = sessionId;
    // Clicking a row in one section clears the other's selection so the
    // "selection" concept stays singular from the user's POV.
    if (kind === "chat" && selectedTaskIds.size > 0)
      setSelectedTaskIds(new Set());
    if (kind === "agent-task" && selectedChatIds.size > 0)
      setSelectedChatIds(new Set());
  };

  // ─── Right-click → menu ─────────────────────────────────────────
  const handleRowContextMenu = (
    kind: SectionKind,
    sessionId: string,
    e: React.MouseEvent,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canEdit) return;
    const current = selectionFor(kind);
    let targetIds: string[];
    if (current.has(sessionId) && current.size > 1) {
      targetIds = Array.from(current);
    } else {
      targetIds = [sessionId];
      if (!current.has(sessionId)) {
        setSelectionFor(kind, new Set([sessionId]));
        lastIdRefFor(kind).current = sessionId;
      }
    }
    setMenu({ x: e.clientX, y: e.clientY, kind, targetIds });
  };

  const sessionsByKind = (kind: SectionKind) =>
    kind === "chat" ? mainSessions : agentTaskSessions;

  const deleteTargets = async (kind: SectionKind, targets: FlowSession[]) => {
    const ids = targets.map((t) => t.id);
    const labels = SECTION_LABEL[kind];

    const results = await Promise.allSettled(
      ids.map(async (id) => {
        await deleteFlowSession.mutateAsync(id);
        // Tear down anything on the canvas that was anchored to this
        // session — quick-chat `chatSession` node, persisted process
        // info on owning nodes, and any tray entry. Fired only on a
        // successful API delete so a network failure can't orphan the
        // user's canvas state.
        emitCanvasFlowSessionDeleted(id);
      }),
    );
    const failed = results.filter((r) => r.status === "rejected").length;
    const succeeded = ids.length - failed;
    if (succeeded > 0) {
      toast.success(
        succeeded === 1
          ? "Archived."
          : `Archived ${succeeded} ${labels.plural}.`,
      );
    }
    if (failed > 0) {
      toast.error(
        `Failed to delete ${failed} ${failed === 1 ? labels.singular : labels.plural}`,
      );
    }

    // Clear selection in the affected section.
    if (kind === "chat") setSelectedChatIds(new Set());
    else setSelectedTaskIds(new Set());
    queryClient.invalidateQueries({
      queryKey: ["canvas-flow-sessions", canvasId],
    });
  };

  const openConfirmForMenu = () => {
    if (!menu) return;
    const all = sessionsByKind(menu.kind);
    const ids =
      menu.targetIds.length > 0
        ? menu.targetIds
        : Array.from(selectionFor(menu.kind));
    const targets = all.filter((s) => ids.includes(s.id));
    if (targets.length === 0) {
      setMenu(null);
      return;
    }
    setMenu(null);
    // Single deletes are one-click (trash is restorable); only bulk deletes
    // keep the confirmation so a stray click can't sweep a whole section.
    if (targets.length === 1) {
      void deleteTargets(menu.kind, targets);
      return;
    }
    setConfirm({ kind: menu.kind, targets });
  };

  const performDelete = async () => {
    if (!confirm) return;
    const { kind, targets } = confirm;
    setConfirm(null);
    await deleteTargets(kind, targets);
  };

  // ─── Render ─────────────────────────────────────────────────────
  if (sessionsLoading) {
    return null;
  }

  return (
    <>
      <div className="flex flex-col">
        {mainSessions.length === 0 ? (
          // Always say so — with the drawer's verb chips retired, an editor's
          // empty section would otherwise render as a bare header.
          <SectionEmptyState message="No conversations yet." />
        ) : (
          mainSessions.map((s) => (
            <SessionRow
              key={s.id}
              sessionId={s.id}
              canvasId={canvasId}
              searchQuery={searchQuery}
              canEdit={canEdit}
              isPlaced={placedNodeBySessionId.has(s.id)}
              selected={selectedChatIds.has(s.id)}
              onSelect={(e) => handleSelect("chat", s.id, e)}
              onContextMenu={(e) => handleRowContextMenu("chat", s.id, e)}
            />
          ))
        )}

        {agentTaskSessions.length > 0 ? (
          <>
            <p className="mx-2 mt-2 px-2 font-mono text-2xs font-medium uppercase tracking-[0.08em] text-n-50">
              Agent tasks
            </p>
            {agentTaskSessions.map((s) => (
              <SessionRow
                key={s.id}
                sessionId={s.id}
                canvasId={canvasId}
                searchQuery={searchQuery}
                canEdit={canEdit}
                isPlaced={placedNodeBySessionId.has(s.id)}
                variant="agent-task"
                selected={selectedTaskIds.has(s.id)}
                onSelect={(e) => handleSelect("agent-task", s.id, e)}
                onContextMenu={(e) =>
                  handleRowContextMenu("agent-task", s.id, e)
                }
              />
            ))}
          </>
        ) : null}
      </div>

      {menu && menu.targetIds.length > 0 && (
        <RailRowMenu
          kebab={false}
          label="Chat"
          open
          onOpenChange={(o) => {
            if (!o) setMenu(null);
          }}
          point={{ x: menu.x, y: menu.y }}
        >
          {/*
            Open: only meaningful for a single target — opens the
            floating chat panel for that flow session. Mirrors the
            row's double-click behaviour so right-click → Open
            matches what users discover by accident.
          */}
          {menu.targetIds.length === 1 && (
            <DropdownMenuItem
              onClick={() => {
                const targetId = menu.targetIds[0];
                emitCanvasOpenFloatingSession({
                  canvasId,
                  flowSessionId: targetId,
                });
              }}
            >
              <ExternalLink />
              Open
            </DropdownMenuItem>
          )}
          {/*
            Locate: only meaningful for a single target (multi-select
            "locate" doesn't have a single canvas position to focus
            on). When the chat has a `chatSession` node placement,
            dispatch `canvas:focus-node` and close the menu. When it
            has none, the entry stays visible but disabled with a
            "Not placed" label so the menu's row count is consistent
            regardless of placement state.
          */}
          {menu.targetIds.length === 1 &&
            (() => {
              const targetId = menu.targetIds[0];
              const placedNodeId = placedNodeBySessionId.get(targetId) ?? null;
              const isPlaced = placedNodeId !== null;
              return (
                <DropdownMenuItem
                  disabled={!isPlaced}
                  onClick={() => {
                    if (!isPlaced || !placedNodeId) return;
                    window.dispatchEvent(
                      new CustomEvent("canvas:focus-node", {
                        detail: { nodeId: placedNodeId, keepZoom: true },
                      }),
                    );
                  }}
                >
                  <Crosshair />
                  {isPlaced ? "Locate on canvas" : "Not placed on canvas"}
                </DropdownMenuItem>
              );
            })()}
          {/*
            Remove from canvas: dispatches a `canvas:remove-node` for
            every target in the selection that has a `chatSession`
            node placement. The flow session itself stays — the row
            simply transitions back to the dimmed "Not placed"
            treatment. Hidden when no target in the selection is
            placed (matches `CanvasScope`'s `hasPlaced` guard so the
            entry only appears when it can actually do something).
          */}
          {(() => {
            const placedNodeIds = menu.targetIds
              .map((id) => placedNodeBySessionId.get(id))
              .filter((nid): nid is string => !!nid);
            if (placedNodeIds.length === 0) return null;
            return (
              <DropdownMenuItem
                onClick={() => {
                  for (const nodeId of placedNodeIds) {
                    window.dispatchEvent(
                      new CustomEvent("canvas:remove-node", {
                        detail: { nodeId },
                      }),
                    );
                  }
                }}
              >
                <Trash2 />
                {placedNodeIds.length > 1
                  ? `Remove ${placedNodeIds.length} from canvas`
                  : "Remove from canvas"}
              </DropdownMenuItem>
            );
          })()}
          <DropdownMenuItem danger onClick={() => openConfirmForMenu()}>
            <Trash2 />
            Archive
          </DropdownMenuItem>
        </RailRowMenu>
      )}

      <AlertDialog
        open={!!confirm}
        onOpenChange={(open) => {
          if (!open) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm ? "Archive?" : ""}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm
                ? confirm.targets.length > 1
                  ? `These ${SECTION_LABEL[confirm.kind].plural} will be archived. You can restore them any time.`
                  : `This ${SECTION_LABEL[confirm.kind].singular} will be archived. You can restore it any time.`
                : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                performDelete();
              }}
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Single chat / agent-task row.
 *
 * Selection + open semantics (the rail-wide single-click model):
 *   - Plain click delegates to the parent `onSelect` (which records a
 *     single-item selection) and then opens the floating session.
 *   - Cmd/ctrl/shift clicks only build the multi-selection — they never
 *     open, so bulk operations stay possible.
 *   - Right-click bubbles up via `onContextMenu` so the parent can
 *     reconcile menu state against the active selection.
 *   - Text is `select-none` so shift-click range-select doesn't paint
 *     a text highlight underneath the row's selection background.
 */
function SessionRow({
  sessionId,
  canvasId,
  searchQuery,
  canEdit,
  isPlaced,
  variant = "chat",
  selected,
  onSelect,
  onContextMenu,
}: {
  sessionId: string;
  canvasId: string;
  searchQuery: string;
  canEdit: boolean;
  /** True when a `chatSession` node references this session on the
   *  canvas. Drives the same dimmed + leading-tooltip treatment
   *  unplaced files get in `CanvasScope` so the placement state
   *  reads consistently across the explorer. */
  isPlaced: boolean;
  variant?: "chat" | "agent-task";
  selected: boolean;
  onSelect: (e: React.MouseEvent) => void;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const { data: session, isPending: isSessionPending } = useFlowSessionById(
    sessionId,
    {
      enabled: !!sessionId,
    },
  );

  const isAgentTask = variant === "agent-task";
  const name =
    session?.name?.trim() || (isAgentTask ? "Agent task" : "Canvas Agent");

  const isProcessing = session?.status === "processing";
  // Flow session statuses considered "done" for agent tasks. Mirrors
  // the mapping in `useFlowSessionLiveStatus` (ready / enabled →
  // complete) without pulling in the heavier per-row tool-call query
  // that hook also runs. Chats stay neutral when complete — only
  // agent tasks pick up the green palette so the user can scan the
  // list and see at a glance which background tasks have finished.
  const isAgentTaskDone =
    isAgentTask &&
    !isProcessing &&
    (session?.status === "ready" || session?.status === "enabled");

  const handleOpen = () => {
    emitCanvasOpenFloatingSession({ canvasId, flowSessionId: sessionId });
  };

  // Wired through the same `CanvasDndProvider` dispatcher every other
  // explorer row uses, so the drag picks up the unified `CanvasDragPill`
  // overlay, drop-target highlighting on the canvas body / sections,
  // the 8px activation distance that disambiguates click-to-select,
  // and KeyboardSensor accessibility.
  const draggableData: DraggableData = useMemo(
    () => ({
      kind: "chat-session",
      payload: {
        flowSessionId: sessionId,
        sourceCanvasId: canvasId,
        name,
      },
    }),
    [sessionId, canvasId, name],
  );
  const draggable = useDraggable({
    id: `chat-session-row:${sessionId}`,
    data: draggableData,
    disabled: !canEdit,
  });

  // Filter applied AFTER all hooks above so the count of hooks rendered
  // by this component stays stable across re-renders — toggling search
  // visibility via an early return earlier in the body would violate
  // the rules of hooks (see React "rendered fewer hooks than expected").
  if (searchQuery && !name.toLowerCase().includes(searchQuery.toLowerCase())) {
    return null;
  }

  const row = (
    <div
      ref={draggable.setNodeRef}
      data-explorer-row="true"
      {...draggable.attributes}
      {...draggable.listeners}
      className={cn(
        "group flex min-h-7 items-center gap-2.5 px-2 cursor-pointer select-none",
        "text-base text-n-80",
        !selected && "rounded-md",
        selected ? "bg-foreground/[0.04] text-foreground" : "hover:bg-n-05",
        // Mirror `ExplorerFileRow`'s "Not placed on canvas" treatment
        // so the placement state is communicated identically across
        // chat rows and file rows in the same pane.
        !isPlaced && "opacity-60",
      )}
      data-rail-list-item=""
      data-rail-chrome=""
      {...(selected ? { "data-rail-selected": "" } : {})}
      // Modifier clicks build a multi-selection without opening; a plain
      // click selects the row and opens it — the canvas pane's click model.
      onClick={(e) => {
        if (e.shiftKey) e.preventDefault();
        onSelect(e);
        if (!e.metaKey && !e.ctrlKey && !e.shiftKey) handleOpen();
      }}
      onContextMenu={(e) => {
        if (!canEdit) return;
        onContextMenu(e);
      }}
      style={
        draggable.isDragging ? { opacity: 0.4, cursor: "grabbing" } : undefined
      }
    >
      {/*
        Per-row activity indicator. While the underlying flow session
        is processing we swap the agent / chat icon for a spinner —
        same affordance the (now-hidden) `CanvasProcessTray` used to
        surface. The amber tint matches the tray so users coming from
        the old surface see a familiar "this thing is working" cue.
        Idle rows keep the variant-specific palette (violet for agent
        tasks, neutral for chats).
      */}
      <span
        className={cn(
          "flex size-3.5 shrink-0 items-center justify-center",
          isProcessing
            ? "text-lc-warning"
            : isAgentTaskDone
              ? "text-emerald-600 dark:text-emerald-400"
              : isAgentTask
                ? "text-spectrum-violet"
                : "text-n-50",
        )}
      >
        {isProcessing ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : isAgentTask ? (
          <Bot className="size-3.5" />
        ) : (
          <ChatDot />
        )}
      </span>
      <div className="flex-1 min-w-0">
        {isSessionPending ? (
          <Skeleton className="h-[14px] w-[min(100%,10rem)] rounded" />
        ) : (
          // One line, like the chats rail — the timestamp rides as trailing
          // mono meta instead of a second line.
          <p className="min-w-0 flex-1 truncate text-base">{name}</p>
        )}
      </div>
    </div>
  );

  // Wrap the dimmed row in a Radix tooltip so hovering anywhere on
  // it surfaces the same "Not placed on canvas" hint that
  // `ExplorerFileRow` uses. The 500ms delay matches the file row's
  // and keeps the tooltip from feeling intrusive during ordinary
  // mousing across the list.
  if (!isPlaced) {
    return (
      <Tooltip delayDuration={500}>
        <TooltipTrigger asChild>
          <div>{row}</div>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={4}>
          Not placed on canvas
        </TooltipContent>
      </Tooltip>
    );
  }

  return row;
}
